import { useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { Input } from "../../components/ui/Input";
import { errorMessage, recordedLabel } from "../cockpit/format";
import {
  confidenceLabel,
  epaReportUrl,
  formatCoordinates,
  formatLocation,
  methodLabel,
  reviewLabel,
  reviewVariant,
  segmentVariant,
} from "./format";
import { GroupPickerDialog } from "./GroupPickerDialog";
import { setFacilityGroups } from "./groupsApi";
import { LocationCheckPanel } from "./LocationCheckPanel";
import { PlantNameField } from "./PlantNameField";
import { setFacilityName, setReview } from "./reviewApi";
import type { FacilityDetail, FacilityReviewStatus, PlantNameChange, PlantNameSource } from "./types";

interface Props {
  registryId: string;
  canReview: boolean;
  onClose: () => void;
  onChanged: () => void;
}

function statusChip(isActive: boolean | null, text: string | null) {
  if (isActive === true) return <Chip variant="ok">{text ?? "Active"}</Chip>;
  if (isActive === false) return <Chip variant="bad">{text ?? "Inactive"}</Chip>;
  return <Chip>{text || "Unknown"}</Chip>;
}

export function FacilityDetailDialog({ registryId, canReview, onClose, onChanged }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [detail, setDetail] = useState<FacilityDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState<"verify" | "edit" | null>(null);
  const [draftName, setDraftName] = useState<{ value: string; source: PlantNameSource } | null>(null);

  const saveName = async (name: string, source: PlantNameSource) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await setFacilityName(apiFetchRef.current, registryId, name, source);
      setDetail(updated);
      setDraftName(null);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const review = async (status: FacilityReviewStatus, groupIds?: readonly number[], rename?: PlantNameChange) => {
    setSaving(true);
    setError(null);
    try {
      setDetail(await setReview(apiFetchRef.current, registryId, status, note, groupIds, rename));
      setNote("");
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveGroups = async (groupIds: readonly number[]) => {
    setSaving(true);
    setError(null);
    try {
      setDetail(await setFacilityGroups(apiFetchRef.current, registryId, groupIds));
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmPick = (groupIds: number[], rename?: PlantNameChange) => {
    const mode = picking;
    setPicking(null);
    if (mode === "verify") void review("Verified", groupIds, rename);
    else if (mode === "edit") void saveGroups(groupIds);
  };

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    (async () => {
      try {
        const response = await apiFetchRef.current(`facilities/${encodeURIComponent(registryId)}`);
        const body: FacilityDetail = await response.json();
        if (!cancelled) setDetail(body);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registryId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !picking) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, picking]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(12,14,10,0.65)] z-[1000] p-6"
      role="presentation"
      onClick={onClose}
    >
      {picking && detail && (
        <GroupPickerDialog
          facilityName={detail.name}
          originalName={detail.originalName}
          confirmLabel={picking === "verify" ? "Verify" : "Save Groups"}
          confirmIcon={picking === "verify" ? "verified" : "check"}
          initialIds={detail.polymerGroups.map((g) => g.polymerGroupId)}
          canAdd={canReview}
          locationRegistryId={picking === "verify" ? registryId : undefined}
          canCheckLocation={canReview}
          onLocationApplied={(updated) => {
            setDetail(updated);
            onChanged();
          }}
          onConfirm={confirmPick}
          onClose={() => setPicking(null)}
        />
      )}
      <Card
        className="w-full max-w-3xl max-h-full overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-label="Facility details"
        onClick={(e) => e.stopPropagation()}
      >
        {error && <p className="text-rs-red text-[13px] mt-0">{error}</p>}
        {!detail && !error && <p className="text-rs-light-gray text-[13px]">Loading…</p>}
        {detail && (
          <>
            <div className="flex items-start justify-between gap-4 mb-1">
              <h3 className="m-0 text-lg">{detail.name}</h3>
              <Btn variant="ghost" onClick={onClose} aria-label="Close">
                Close
              </Btn>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <Chip variant={segmentVariant(detail.segment)}>{detail.segment}</Chip>
              {detail.reviewStatus !== "Unclassified" && (
                <Chip variant={reviewVariant(detail.reviewStatus)}>{reviewLabel(detail.reviewStatus)}</Chip>
              )}
              {detail.dataSource === "Manual" && <Chip variant="info">Manual</Chip>}
              {detail.candidateReason && <Chip variant="info">Possible match, not confirmed in scope</Chip>}
              {detail.isRetired && <Chip variant="bad">Retired: no longer in EPA's file</Chip>}
              {detail.supplierName && <Chip variant="ok">Supplier: {detail.supplierName}</Chip>}
            </div>

            <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-2 text-[13px] mb-5">
              <dt className="text-rs-light-gray">Address</dt>
              <dd className="m-0">
                {[detail.address, detail.supplementalLocation].filter(Boolean).join(", ") || "—"}
                <br />
                {formatLocation(detail.city, detail.stateCode)} {detail.postalCode ?? ""}
                {detail.countyName ? ` · ${detail.countyName} County` : ""}
              </dd>
              <dt className="text-rs-light-gray">Coordinates</dt>
              <dd className="m-0">
                {formatCoordinates(detail.latitude, detail.longitude)}
                {detail.geocodeSource === "Census" && " (looked up from the address)"}
                {detail.geocodeSource === "Azure" && (
                  <>
                    {" "}
                    <Chip variant="blue">Moved to address</Chip> taken from the address and checked on the map
                  </>
                )}
                {detail.accuracyValue !== null &&
                  detail.geocodeSource === "Epa" &&
                  ` · accurate to about ${Math.round(detail.accuracyValue)} m`}
              </dd>
              {detail.dataSource === "Manual" ? (
                <>
                  <dt className="text-rs-light-gray">Source</dt>
                  <dd className="m-0">Added by hand, {recordedLabel(detail.firstSeenUtc)}</dd>
                </>
              ) : (
                <>
                  <dt className="text-rs-light-gray">EPA registry id</dt>
                  <dd className="m-0">
                    <a
                      className="text-rs-green"
                      href={detail.detailReportUrl ?? epaReportUrl(detail.registryId)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {detail.registryId}
                    </a>
                  </dd>
                  <dt className="text-rs-light-gray">In EPA's file since</dt>
                  <dd className="m-0">
                    {recordedLabel(detail.firstSeenUtc)}; last seen {recordedLabel(detail.lastSeenUtc)}
                  </dd>
                </>
              )}
              {detail.externalFacilityId && (
                <>
                  <dt className="text-rs-light-gray">Facility ID</dt>
                  <dd className="m-0 mono">{detail.externalFacilityId}</dd>
                </>
              )}
              {detail.groups && (
                <>
                  <dt className="text-rs-light-gray">Groups</dt>
                  <dd className="m-0">{detail.groups}</dd>
                </>
              )}
              {detail.profiles && (
                <>
                  <dt className="text-rs-light-gray">Profile</dt>
                  <dd className="m-0">{detail.profiles}</dd>
                </>
              )}
            </dl>

            {canReview && (
              <div className="mb-5 text-[13px]">
                <PlantNameField
                  value={draftName?.value ?? detail.name}
                  currentName={detail.name}
                  originalName={detail.originalName}
                  source={draftName?.source ?? "Manual"}
                  disabled={saving}
                  onChange={(value, source) => setDraftName({ value, source })}
                />
                {draftName && draftName.value.trim() !== detail.name && (
                  <div className="flex gap-2 mt-2">
                    <Btn disabled={saving} onClick={() => void saveName(draftName.value, draftName.source)}>
                      <span className="material-symbols-sharp">check</span>Save Name
                    </Btn>
                    <Btn variant="ghost" disabled={saving} onClick={() => setDraftName(null)}>
                      Cancel
                    </Btn>
                  </div>
                )}
              </div>
            )}

            <LocationCheckPanel
              registryId={registryId}
              canCheck={canReview}
              onApplied={(updated) => {
                setDetail(updated);
                onChanged();
              }}
              onUseName={canReview ? (name) => setDraftName({ value: name, source: "Map" }) : undefined}
            />

            <h4 className="m-0 mb-2 text-sm">ResinSmart Review</h4>
            <div className="mb-5 text-[13px]">
              {canReview && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Input
                    className="max-w-[320px]"
                    value={note}
                    maxLength={500}
                    placeholder="Note (optional)"
                    aria-label="Review note"
                    onChange={(e) => setNote(e.target.value)}
                  />
                  {detail.reviewStatus !== "Verified" && detail.reviewStatus !== "Rejected" && (
                    <Btn disabled={saving} onClick={() => setPicking("verify")}>
                      <span className="material-symbols-sharp">verified</span>Verify
                    </Btn>
                  )}
                  {detail.reviewStatus === "Rejected" && (
                    <Btn disabled={saving} variant="ghost" onClick={() => void review("Unclassified")}>
                      <span className="material-symbols-sharp">restore_from_trash</span>Restore
                    </Btn>
                  )}
                  {(detail.reviewStatus === "Verified" || detail.polymerGroups.length > 0) && (
                    <Btn disabled={saving} variant="ghost" onClick={() => setPicking("edit")}>
                      <span className="material-symbols-sharp">category</span>Edit Groups
                    </Btn>
                  )}
                  {detail.reviewStatus === "Verified" && (
                    <Btn disabled={saving} variant="ghost" onClick={() => void review("Unclassified")}>
                      <span className="material-symbols-sharp">remove_done</span>Remove Verification
                    </Btn>
                  )}
                  {detail.reviewStatus !== "Rejected" && (
                    <Btn disabled={saving} variant="ghost" onClick={() => void review("Rejected")}>
                      <span className="material-symbols-sharp">delete</span>Reject
                    </Btn>
                  )}
                </div>
              )}
              {detail.reviews.length === 0 ? (
                <p className="m-0 text-rs-light-gray">Nobody has reviewed this facility yet.</p>
              ) : (
                <ul className="m-0 p-0 list-none flex flex-col gap-1">
                  {detail.reviews.map((r, index) => (
                    <li key={`${r.reviewedUtc}-${index}`}>
                      {r.toStatus === "Verified"
                        ? "Verified"
                        : r.toStatus === "Rejected"
                          ? "Rejected"
                          : "Set to Unclassified"}{" "}
                      by {r.reviewedByName ?? "an unknown user"}{" "}
                      <span className="text-rs-light-gray">
                        · {recordedLabel(r.reviewedUtc)}
                        {r.source === "Upload" ? " · by file upload" : ""}
                        {r.note ? ` · ${r.note}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <h4 className="m-0 mb-2 text-sm">NAICS Codes</h4>
            <ul className="m-0 mb-5 p-0 list-none text-[13px] flex flex-col gap-1">
              {detail.naics.length === 0 && <li className="text-rs-light-gray">None on file.</li>}
              {detail.naics.map((n) => (
                <li key={n.code}>
                  <span className="mono">{n.code}</span> {n.description}
                  {n.isPrimary && (
                    <Chip className="ml-2" variant="ok">
                      Primary
                    </Chip>
                  )}
                  {!n.isInScope && <Chip className="ml-2">Other</Chip>}
                </li>
              ))}
            </ul>

            <h4 className="m-0 mb-2 text-sm">
              Companies <span className="text-rs-light-gray font-normal">({detail.organizations.length})</span>
            </h4>
            <ul className="m-0 mb-5 p-0 list-none text-[13px] flex flex-col gap-1">
              {detail.organizations.length === 0 && <li className="text-rs-light-gray">None on file.</li>}
              {detail.organizations.map((o) => (
                <li key={`${o.affiliationType}-${o.orgName}`}>
                  {o.orgName} <span className="text-rs-light-gray">· {o.affiliationType.toLowerCase()}</span>
                  {o.dunsNumber && <span className="text-rs-light-gray"> · DUNS {o.dunsNumber}</span>}
                </li>
              ))}
            </ul>

            <h4 className="m-0 mb-2 text-sm">
              EPA Programs{" "}
              <span className="text-rs-light-gray font-normal">
                ({detail.activeProgramCount} active of {detail.programs.length})
              </span>
            </h4>
            <div className="card p-0 overflow-auto max-h-[220px] mb-5">
              <table>
                <tbody>
                  {detail.programs.map((p) => (
                    <tr key={`${p.pgmSysAcrnm}-${p.pgmSysId}-${p.interestType}`}>
                      <td className="mono">{p.pgmSysAcrnm}</td>
                      <td className="mono">{p.pgmSysId}</td>
                      <td>{p.interestType || "—"}</td>
                      <td>{statusChip(p.isActive, p.activeStatus)}</td>
                    </tr>
                  ))}
                  {detail.programs.length === 0 && (
                    <tr>
                      <td>None on file.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {detail.matches.length > 0 && (
              <>
                <h4 className="m-0 mb-2 text-sm">Supplier Matches</h4>
                <ul className="m-0 p-0 list-none text-[13px] flex flex-col gap-1">
                  {detail.matches.map((m) => (
                    <li key={m.matchId}>
                      {m.candidateSupplierName ?? "No candidate"}{" "}
                      <span className="text-rs-light-gray">
                        · {methodLabel(m.method)}
                        {confidenceLabel(m.confidenceScore) ? ` · ${confidenceLabel(m.confidenceScore)}` : ""}
                      </span>{" "}
                      <Chip
                        variant={m.matchState === "Confirmed" ? "ok" : m.matchState === "Rejected" ? "bad" : "default"}
                      >
                        {m.matchState}
                      </Chip>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
